import React, { useEffect, useState, useRef } from "react";
import { markChecklist } from "../lib/checklist";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, CheckCircle2, Clock, CalendarDays, ChevronLeft, ChevronRight, AtSign, Printer, Star, Trash2, Archive, RotateCcw, Moon, AlertTriangle } from "lucide-react";
import api, { fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useFirma, OperatoreLocked } from "../context/FirmaContext";
import DateFilterBar, { matchDate } from "../components/DateFilterBar";
import { PresaVisioneBanner } from "../components/PresaVisioneBanner";
import { ExportButton } from "../components/ExportButton";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { SedeBadge } from "../lib/sedi";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { useOrariTurni, orarioLabel } from "../lib/orari";
import { ConsegnaTesto, EventoTitolo } from "../components/ConsegnaTesto";
import ConsegneVistaNew, { VistaSwitch } from "../components/ConsegneVistaNew";
import ConsegneVistaAi from "../components/ConsegneVistaAi";

const CATS = ["generale", "salute", "comportamento", "alimentazione", "terapia", "caduta", "alvo", "sonno", "igiene", "visita", "ospedale", "comunicazione familiari", "manutenzione", "altro"];
const SEZIONI = [["mattina", "Mattina", "07:00 - 14:00", "#dcfce7", "#16a34a"], ["pomeriggio", "Pomeriggio", "14:00 - 20:00", "#dbeafe", "#2563eb"], ["notte", "Notte", "20:00 - 07:00", "#ede9fe", "#7c3aed"]];
const bucketOf = (t) => (t === "sera" ? "notte" : (t || "mattina"));
const nowTurno = () => { const h = new Date().getHours(); if (h >= 7 && h < 14) return "mattina"; if (h >= 14 && h < 20) return "pomeriggio"; return "notte"; };
const PRIO = { urgente: 0, importante: 1, normale: 2 };
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const ACCENT = { urgente: "#dc2626", importante: "#d97706", normale: "#e2e8f0" };
const PRIO_LABEL = { urgente: ["Urgente", "text-red-600"], importante: ["Importante", "text-amber-600"] };
const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const capFirst = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const fmtGiorno = (iso) => { try { return capFirst(new Date(iso + "T00:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long" })); } catch { return iso; } };
const fmtGiornoLungo = (iso) => { try { return capFirst(new Date(iso + "T00:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })); } catch { return iso || ""; } };

export default function Consegne() {
  const { selectedStructure, user, structures, can, opSections = [] } = useAuth();
  const { opName, signerName, activeOperatore, firmaEnabled } = useFirma();
  const readerName = opName || (!firmaEnabled ? (user?.name || "").trim() : "");
  const canArchivio = activeOperatore ? (opSections || []).includes("consegne_archivio") : can("consegne_archivio");
  const qc = useQueryClient();
  const orari = useOrariTurni(selectedStructure);
  const sezioni = SEZIONI.map(([k, l, , bg, bar]) => [k, l, orarioLabel(orari[k]), bg, bar]);
  const hm2m = (s) => { const [h, m] = String(s).split(":").map(Number); return (h || 0) * 60 + (m || 0); };
  const curTurno = () => {
    const now = new Date(); const mins = now.getHours() * 60 + now.getMinutes();
    const sm = hm2m(orari.mattina?.inizio || "07:00"), sp = hm2m(orari.pomeriggio?.inizio || "14:00"), sn = hm2m(orari.notte?.inizio || "20:00");
    if (mins >= sm && mins < sp) return "mattina";
    if (mins >= sp && mins < sn) return "pomeriggio";
    return "notte";
  };
  const [open, setOpen] = useState(false);
  const [vista, setVistaS] = useState(() => localStorage.getItem("consegne-vista") || "attuale");
  const setVista = (v) => { localStorage.setItem("consegne-vista", v); setVistaS(v); };
  const [futureWarn, setFutureWarn] = useState(null);
  const [notteDlg, setNotteDlg] = useState(null); // { data } popup data consegna notturna
  const [form, setForm] = useState({});
  const [ospiti, setOspiti] = useState([]);
  const [operatori, setOperatori] = useState([]);
  const [filterPrio, setFilterPrio] = useState("all");
  const [filterTurno, setFilterTurno] = useState("all");
  const [filterDate, setFilterDate] = useState("all");
  const [customDate, setCustomDate] = useState("");
  const [readers, setReaders] = useState({});
  const [page, setPage] = useState(0);
  const [tagged, setTagged] = useState([]);
  const [mention, setMention] = useState(null);
  const [detail, setDetail] = useState(null);
  const [delConsegna, setDelConsegna] = useState(null);
  const [archivioOpen, setArchivioOpen] = useState(false);
  const [archQ, setArchQ] = useState("");
  const [archData, setArchData] = useState("");
  const [archTurno, setArchTurno] = useState("tutti");
  const printConsegna = (c) => {
    const esc = (s) => String(s || "").replace(/[&<>]/g, (x) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[x]));
    const tags = (c.ospiti_tag || []).map((o) => `@${esc(o.nome)}`).join(" ");
    const w = window.open("", "_blank", "width=800,height=900");
    if (!w) { toast.error("Consenti i popup per stampare"); return; }
    w.document.write(`<html><head><title>Consegna</title><style>
      body{font-family:system-ui,Arial,sans-serif;padding:40px;color:#0f172a;line-height:1.6}
      h1{margin:0 0 4px;font-size:24px} .meta{color:#475569;font-size:14px;margin-bottom:8px}
      .prio{display:inline-block;font-weight:700;text-transform:uppercase;font-size:12px;padding:2px 8px;border-radius:999px;background:#fee2e2;color:#dc2626}
      .tags{margin:8px 0;color:#1d4ed8;font-weight:600} .testo{font-size:20px;white-space:pre-wrap;margin-top:16px;border-top:1px solid #e2e8f0;padding-top:16px}
    </style></head><body>
      <h1>Consegna${c.priorita && c.priorita !== "normale" ? ` <span class="prio">${esc(c.priorita)}</span>` : ""}</h1>
      <div class="meta"><b>${esc(c.operatore_nome || "—")}</b> · ${esc(fmtDate(c.data))} ${esc(c.ora || "")} · turno ${esc(bucketOf(c.turno))}</div>
      ${tags ? `<div class="tags">${tags}</div>` : ""}
      <div class="testo">${esc(c.testo)}</div>
    </body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 300);
  };
  const taRef = useRef(null);

  const params = { structure_id: selectedStructure };
  const { data = [] } = useQuery({ queryKey: ["consegne", selectedStructure], queryFn: async () => (await api.get("/consegne", { params })).data });
  const { data: turniToday = [] } = useQuery({ queryKey: ["turni-consegne", selectedStructure], queryFn: async () => (await api.get("/turni", { params })).data, refetchInterval: 30000 });
  const { data: archivio = [] } = useQuery({ queryKey: ["consegne-archivio", selectedStructure], enabled: archivioOpen && canArchivio, queryFn: async () => (await api.get("/consegne/archivio", { params })).data });
  const archivioFiltered = archivio.filter((c) => {
    if (archTurno !== "tutti" && bucketOf(c.turno) !== archTurno) return false;
    if (archData && (c.data || "") !== archData) return false;
    if (archQ) { const q = archQ.toLowerCase(); if (!((c.testo || "").toLowerCase().includes(q) || (c.operatore_nome || "").toLowerCase().includes(q))) return false; }
    return true;
  });
  const eliminaConsegna = async (c) => {
    try {
      await api.post(`/consegne/${c.id}/elimina`);
      toast.success("Consegna spostata in archivio");
      setDelConsegna(null); setDetail(null);
      qc.invalidateQueries({ queryKey: ["consegne"] });
      qc.invalidateQueries({ queryKey: ["consegne-archivio"] });
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const ripristinaConsegna = async (c) => {
    try {
      await api.post(`/consegne/${c.id}/ripristina`);
      toast.success("Consegna ripristinata");
      qc.invalidateQueries({ queryKey: ["consegne"] });
      qc.invalidateQueries({ queryKey: ["consegne-archivio"] });
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  useEffect(() => { setPage(0); }, [filterDate, filterPrio, filterTurno, customDate, selectedStructure]);

  useEffect(() => { if (open) api.get("/ospiti", { params }).then((r) => setOspiti(r.data)); }, [open, selectedStructure]);
  useEffect(() => { if (open) api.get("/firma/operatori", { params: { structure_id: form.structure_id || selectedStructure } }).then((r) => setOperatori(r.data)); }, [open, form.structure_id, selectedStructure]);

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const onTestoChange = (e) => {
    const val = e.target.value; setF("testo", val);
    const pos = e.target.selectionStart || val.length;
    const m = val.slice(0, pos).match(/@([\p{L}\s'’]*)$/u);
    if (m) setMention({ query: (m[1] || "").toLowerCase().trim(), start: pos - m[0].length }); else setMention(null);
  };
  const pickOspite = (o) => {
    const nome = `${o.nome} ${o.cognome}`;
    const ta = taRef.current; const pos = ta ? ta.selectionStart : (form.testo || "").length;
    const start = mention?.start ?? pos;
    const before = (form.testo || "").slice(0, start); const after = (form.testo || "").slice(pos);
    const insert = `@${nome} `; const caret = before.length + insert.length;
    setF("testo", `${before}${insert}${after}`);
    setTagged((p) => (p.some((x) => x.id === o.id) ? p : [...p, { id: o.id, nome }]));
    setMention(null);
    setTimeout(() => { if (ta) { ta.focus(); ta.setSelectionRange(caret, caret); } }, 0);
  };
  const suggestions = mention ? ospiti.filter((o) => `${o.nome} ${o.cognome}`.toLowerCase().includes(mention.query)).slice(0, 6) : [];
  const renderTesto = (c) => {
    const names = (c.ospiti_tag || []).map((o) => o.nome).sort((a, b) => b.length - a.length);
    if (!names.length) return c.testo;
    const esc = names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    const re = new RegExp(`@(${esc.join("|")})`, "g");
    const out = []; let last = 0; let m; let k = 0;
    while ((m = re.exec(c.testo))) {
      if (m.index > last) out.push(c.testo.slice(last, m.index));
      out.push(<span key={k++} className="font-semibold text-blue-700 bg-blue-50 rounded px-1">@{m[1]}</span>);
      last = m.index + m[0].length;
    }
    if (last < c.testo.length) out.push(c.testo.slice(last));
    return out;
  };
  const openNew = () => {
    const init = { data: new Date().toISOString().slice(0, 10), ora: new Date().toTimeString().slice(0, 5), operatore_nome: signerName || "", priorita: "normale", turno: curTurno() };
    const defStruct = selectedStructure !== "all" ? selectedStructure : ((structures || []).length === 1 ? structures[0].id : undefined);
    if (defStruct) init.structure_id = defStruct;
    setTagged([]); setMention(null); setForm(init); setOpen(true);
  };
  const save = async () => {
    if (!form.structure_id) { toast.error("Seleziona una struttura specifica in alto."); return; }
    if (!form.operatore_nome || !form.operatore_nome.trim()) { toast.error("Indica il nome dell'operatore che scrive la consegna."); return; }
    if (!form.testo || !form.testo.trim()) { toast.error("Inserisci il testo della consegna."); return; }
    const nowIso = new Date().toISOString().slice(0, 10);
    if (form.data > nowIso) { setFutureWarn({ reason: "giorno" }); return; }
    if (form.data === nowIso) {
      const starts = { mattina: orari.mattina?.inizio || "07:00", pomeriggio: orari.pomeriggio?.inizio || "14:00", notte: orari.notte?.inizio || "20:00", cucina: orari.cucina?.inizio || "07:00", sera: orari.notte?.inizio || "20:00" };
      const nowMin = new Date().getHours() * 60 + new Date().getMinutes();
      if (hm2m(starts[form.turno] || "00:00") > nowMin) { setFutureWarn({ reason: "turno", turno: form.turno }); return; }
    }
    try {
      const activeTags = tagged.filter((t) => (form.testo || "").includes(`@${t.nome}`));
      await api.post("/consegne", { ...form, ospiti_tag: activeTags });
      toast.success("Consegna registrata");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["consegne"] });
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  useEffect(() => { markChecklist("consegne"); }, []);
  const fmtLet = (iso) => { try { const d = new Date(iso); return `${d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" })} ${d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}`; } catch { return ""; } };
  const autoReadRef = useRef(new Set());
  useEffect(() => {
    if (!user?.id || !data.length || !readerName) return;
    const myName = readerName;
    const todo = data.filter((c) => !(c.letture || []).some((l) => l.user_name === myName) && !autoReadRef.current.has(c.id + myName));
    if (!todo.length) return;
    const t = setTimeout(async () => {
      for (const c of todo) { autoReadRef.current.add(c.id + myName); try { await api.post(`/consegne/${c.id}/read`, { operatore_nome: readerName }); } catch { /* noop */ } }
      qc.invalidateQueries({ queryKey: ["consegne"] });
      qc.invalidateQueries({ queryKey: ["consegne-badge"] });
    }, 1500);
    return () => clearTimeout(t);
  }, [data, user?.id, readerName]); // eslint-disable-line

  const filtered = data.filter((c) => (filterPrio === "all" || c.priorita === filterPrio) && matchDate(c.data, filterDate, customDate));
  const grouped = groupByTurno(filtered);
  function groupByTurno(list) {
    const g = { mattina: [], pomeriggio: [], notte: [] };
    list.forEach((c) => g[bucketOf(c.turno)].push(c));
    Object.keys(g).forEach((k) => g[k].sort((a, b) => (PRIO[a.priorita] ?? 3) - (PRIO[b.priorita] ?? 3) || (b.created_at || "").localeCompare(a.created_at || "")));
    return g;
  }

  const myTurno = curTurno();
  const isUnread = (c) => !(c.letture || []).some((l) => l.user_id === user?.id);
  const myTurnoUnread = filtered.filter((c) => bucketOf(c.turno) === myTurno && isUnread(c));
  const unreadByTurno = { mattina: 0, pomeriggio: 0, notte: 0 };
  filtered.forEach((c) => { if (isUnread(c)) unreadByTurno[bucketOf(c.turno)] += 1; });

  const todayIso = ymd(new Date());
  const operatorsByTurno = { mattina: [], pomeriggio: [], notte: [] };
  const leaderByTurno = { mattina: null, pomeriggio: null, notte: null };
  turniToday.filter((t) => t.data === todayIso).forEach((t) => { const k = bucketOf(t.turno); if (operatorsByTurno[k] && t.operatore_nome) { operatorsByTurno[k].push(t.operatore_nome); if (t.leader) leaderByTurno[k] = t.operatore_nome; } });

  const renderCard = (c) => {
    const letture = c.letture || [];
    return (
      <div key={c.id} className="relative rounded-lg border bg-white p-3.5 hover:shadow-md hover:border-primary/40 transition-all cursor-pointer" onClick={() => setDetail(c)} title="Clicca per ingrandire" data-testid={`consegna-${c.id}`} style={{ borderLeft: `3px solid ${ACCENT[c.priorita] || ACCENT.normale}` }}>
        {canArchivio && (
          <button onClick={(e) => { e.stopPropagation(); setDelConsegna(c); }} data-testid={`consegna-del-${c.id}`} title="Elimina consegna (va in archivio)" className="absolute top-2 right-2 text-muted-foreground hover:text-rose-600 p-1 rounded-md hover:bg-rose-50">
            <Trash2 size={14} />
          </button>
        )}
        <div className="text-[11px] mb-1.5 pr-6" data-testid={`consegna-operatore-${c.id}`}><span className="font-semibold text-foreground">{c.operatore_nome || "—"}</span> <span className="text-muted-foreground">· {fmtDate(c.data)} {c.ora}</span></div>
        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
          {(c.ospiti_tag || []).length > 0
            ? c.ospiti_tag.map((o) => <span key={o.id} className="inline-flex items-center"><span className="text-[11px] font-semibold bg-blue-100 text-blue-700 rounded-full px-2 py-0.5">@{o.nome}</span><SedeBadge id={o.id} /></span>)
            : (c.ospite_nome ? <span className="font-semibold text-blue-700 truncate">{c.ospite_nome}</span> : null)}
          {PRIO_LABEL[c.priorita] && <span className={`text-[11px] font-bold uppercase tracking-wide ${PRIO_LABEL[c.priorita][1]}`}>{PRIO_LABEL[c.priorita][0]}</span>}
        </div>
        <EventoTitolo c={c} />
        <p className={`text-foreground/90 leading-relaxed whitespace-pre-wrap ${c.evento_titolo ? "font-head font-bold text-base" : "text-sm"}`}>{renderTesto(c)}</p>
        <div className="mt-2.5 pt-2 border-t text-[11px] text-muted-foreground" data-testid={`consegna-letture-${c.id}`}>
          {letture.length === 0
            ? <span className="inline-flex items-center gap-1"><Clock size={11} /> Non ancora letta</span>
            : <span className="inline-flex items-center gap-1 text-green-700"><CheckCircle2 size={12} /> Letta da {letture.map((l) => `${l.user_name} (${fmtLet(l.created_at)})`).join(", ")}</span>}
        </div>
      </div>
    );
  };

  const sezioniDaMostrare = sezioni.filter(([key]) => filterTurno === "all" || filterTurno === key);

  const cnColor = (structures || []).find((s) => s.id === selectedStructure)?.colore || "#1f7a5c";
  const cnName = selectedStructure === "all" ? "Tutte le strutture" : ((structures || []).find((s) => s.id === selectedStructure)?.nome || "");
  const consegneCols = [
    { header: "Data", accessor: (c) => fmtDate(c.data) },
    { header: "Ora", accessor: "ora" },
    { header: "Turno", accessor: (c) => bucketOf(c.turno) },
    { header: "Ospite", accessor: (c) => c.ospite_nome || "Generale" },
    { header: "Categoria", accessor: "categoria" },
    { header: "Priorità", accessor: "priorita" },
    { header: "Operatore", accessor: "operatore_nome" },
    { header: "Testo", accessor: "testo" },
  ];

  const renderColumns = (g, dayIso, maxH) => {
    const isTodayCol = dayIso === todayIso;
    const relLabel = dayIso === todayIso ? "oggi" : dayIso === ieri ? "ieri" : null;
    return (
    <div className={`grid gap-4 items-start ${filterTurno === "all" ? "lg:grid-cols-3" : "grid-cols-1"}`}>
      {sezioniDaMostrare.map(([key, label, orario, bg, bar]) => (
        <div key={key} data-testid={`sezione-${key}`} className="rounded-xl border overflow-hidden flex flex-col bg-secondary/20">
          <div className="px-4 py-2.5 flex items-center gap-2 border-b bg-white">
            <span className="h-2 w-2 rounded-full" style={{ background: bar }} />
            <span className="text-sm font-bold uppercase tracking-wide">{label}</span>
            <span className="text-[11px] text-muted-foreground tabnum">{orario}</span>
            {relLabel && <span className={`text-[10px] font-bold uppercase tracking-wide rounded-full px-1.5 py-0.5 ${relLabel === "oggi" ? "text-emerald-700 bg-emerald-100" : "text-amber-700 bg-amber-100"}`} data-testid={`sezione-rel-${key}`}>{relLabel}</span>}
            {isTodayCol && key === myTurno && <span className="text-[10px] font-bold uppercase tracking-wide text-primary bg-primary/10 rounded-full px-2 py-0.5" data-testid={`turno-corrente-${key}`}>In corso</span>}
            <div className="ml-auto flex items-center gap-1.5">
              {g[key].filter(isUnread).length > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold grid place-items-center" title="Consegne da leggere" data-testid={`sezione-unread-${key}`}>{g[key].filter(isUnread).length}</span>}
              <span className="text-xs font-semibold text-muted-foreground" data-testid={`sezione-count-${key}`}>{g[key].length}</span>
            </div>
          </div>
          {isTodayCol && operatorsByTurno[key]?.length > 0 && (
            <div className="px-4 py-1.5 text-[11px] text-muted-foreground bg-white border-b" data-testid={`in-turno-${key}`}>
              In turno: <span className="font-medium text-foreground/80">{operatorsByTurno[key].join(", ")}</span>
              {leaderByTurno[key] && <span className="ml-2 inline-flex items-center gap-1 font-semibold text-amber-700" data-testid={`referente-${key}`}><Star size={11} className="fill-amber-400 text-amber-500" /> Referente: {leaderByTurno[key]}</span>}
            </div>
          )}
          <div className="p-2.5 space-y-2.5 overflow-y-auto" style={{ maxHeight: maxH, minHeight: 140 }}>
            {g[key].length === 0
              ? <div className="text-sm text-muted-foreground text-center py-8">Nessuna consegna.</div>
              : g[key].map(renderCard)}
          </div>
        </div>
      ))}
    </div>
    );
  };

  const DAYS_PER_PAGE = 3;
  const giorni = [...new Set(filtered.map((c) => c.data).filter(Boolean))].sort((a, b) => b.localeCompare(a));
  const totalPages = Math.max(1, Math.ceil(giorni.length / DAYS_PER_PAGE));
  const pageClamped = Math.min(page, totalPages - 1);
  const giorniPage = giorni.slice(pageClamped * DAYS_PER_PAGE, pageClamped * DAYS_PER_PAGE + DAYS_PER_PAGE);
  const ieri = (() => { const d = new Date(); d.setDate(d.getDate() - 1); return ymd(d); })();
  const singleDay = filterDate === "today" ? todayIso : filterDate === "yesterday" ? ieri : filterDate === "custom" ? (customDate || null) : null;

  if (vista === "new" || vista === "ai") {
    return (
      <div>
        <VistaSwitch vista={vista} setVista={setVista} />
        <h1 className="text-xl sm:text-2xl font-head font-bold mb-3">Consegne</h1>
        {vista === "ai" ? <ConsegneVistaAi structureId={selectedStructure} /> : <ConsegneVistaNew structureId={selectedStructure} />}
      </div>
    );
  }

  return (
    <div>
      <VistaSwitch vista={vista} setVista={setVista} />
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-xl sm:text-2xl font-head font-bold">Consegne</h1>
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold tabnum px-2.5 py-1 rounded-lg bg-secondary" data-testid="live-clock"><Clock size={14} />{now.toLocaleTimeString("it-IT")}</span>
          </div>
          <p className="text-sm text-muted-foreground capitalize">{now.toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long" })} · conferma la presa visione ad ogni cambio turno</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
          <Select value={filterPrio} onValueChange={setFilterPrio}>
            <SelectTrigger className="w-40 flex-1 sm:flex-none h-11 sm:h-10" data-testid="filter-prio"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le priorità</SelectItem>
              <SelectItem value="normale">Normale</SelectItem>
              <SelectItem value="importante">Importante</SelectItem>
              <SelectItem value="urgente">Urgente</SelectItem>
            </SelectContent>
          </Select>
          <ExportButton filename="consegne" title="Consegne" subtitle={cnName} columns={consegneCols} rows={filtered} color={cnColor} testid="consegne-export" />
          {canArchivio && <Button variant="outline" onClick={() => setArchivioOpen(true)} data-testid="consegne-archivio-btn" className="flex-1 sm:flex-none"><Archive size={16} className="mr-1" /> Archivio</Button>}
          <Button onClick={openNew} data-testid="new-consegna" className="font-semibold gr-mobile-tap flex-1 sm:flex-none"><Plus size={18} className="mr-1" /> Nuova consegna</Button>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <span className="text-sm font-semibold text-muted-foreground">Turno:</span>
        <div className="flex rounded-lg border overflow-hidden" data-testid="filter-turno">
          {[["all", "Tutti"], ...SEZIONI.map(([k, l]) => [k, l])].map(([k, l]) => (
            <button key={k} onClick={() => setFilterTurno(k)} data-testid={`filter-turno-${k}`}
              className={`px-3 h-9 text-sm font-semibold transition-colors ${filterTurno === k ? "bg-primary text-white" : "hover:bg-secondary"}`}>{l}</button>
          ))}
        </div>
        <span className="text-sm font-semibold text-muted-foreground ml-2">Periodo:</span>
        <DateFilterBar mode={filterDate} setMode={setFilterDate} custom={customDate} setCustom={setCustomDate} testidPrefix="consegne-date" />
      </div>

      <div className="flex items-center gap-2 text-sm mb-4 flex-wrap" data-testid="da-leggere-box">
        <span className="text-muted-foreground font-semibold">Da leggere per turno:</span>
        {SEZIONI.map(([k, l, , , bar]) => (
          <span key={k} data-testid={`da-leggere-${k}`}
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${k === myTurno ? "ring-1 ring-primary bg-primary/5" : ""}`}>
            <span className="h-2 w-2 rounded-full" style={{ background: bar }} />
            {l}: <b className="tabnum" data-testid={k === myTurno ? "da-leggere-count" : `da-leggere-count-${k}`}>{unreadByTurno[k]}</b>
            {k === myTurno && <span className="text-[10px] text-primary font-bold">(tuo turno)</span>}
          </span>
        ))}
      </div>


      <PresaVisioneBanner tipo="consegne" titolo="Presa visione consegne" descrizione="Conferma di aver letto tutte le consegne del tuo turno." structureId={selectedStructure} />

      {filterDate === "all" ? (
        <div className="space-y-6" data-testid="consegne-giorni">
          {giorni.length === 0 && <div className="gr-card text-sm text-muted-foreground text-center py-10">Nessuna consegna.</div>}
          {giorniPage.map((giorno) => {
            const gg = groupByTurno(filtered.filter((c) => c.data === giorno));
            const tot = gg.mattina.length + gg.pomeriggio.length + gg.notte.length;
            return (
              <div key={giorno} data-testid={`giorno-${giorno}`}>
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white shadow-sm">
                    <CalendarDays size={18} />
                    <span className="font-head font-bold text-lg" data-testid={`giorno-label-${giorno}`}>{fmtGiorno(giorno)}</span>
                  </div>
                  <span className="text-sm text-muted-foreground tabnum">{tot} consegne</span>
                </div>
                {renderColumns(gg, giorno, "52vh")}
              </div>
            );
          })}
          {giorni.length > DAYS_PER_PAGE && (
            <div className="flex items-center justify-center gap-3 pt-2" data-testid="giorni-pagination">
              <Button variant="outline" size="sm" disabled={pageClamped === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} data-testid="giorni-prev"><ChevronLeft size={16} className="mr-1" /> Più recenti</Button>
              <span className="text-sm font-semibold tabnum" data-testid="giorni-page-indicator">Pagina {pageClamped + 1} di {totalPages}</span>
              <Button variant="outline" size="sm" disabled={pageClamped >= totalPages - 1} onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} data-testid="giorni-next">Più vecchie <ChevronRight size={16} className="ml-1" /></Button>
            </div>
          )}
        </div>
      ) : (
        <div>
          {singleDay && (
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white shadow-sm">
                <CalendarDays size={18} />
                <span className="font-head font-bold text-lg" data-testid="giorno-label-single">{fmtGiorno(singleDay)}</span>
              </div>
              <span className="text-sm text-muted-foreground tabnum">{filtered.length} consegne</span>
            </div>
          )}
          {renderColumns(grouped, singleDay, "72vh")}
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="w-[95vw] max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="consegna-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl flex items-center gap-3 flex-wrap">
                  Consegna
                  {PRIO_LABEL[detail.priorita] && <span className={`text-sm font-bold uppercase tracking-wide ${PRIO_LABEL[detail.priorita][1]}`}>{PRIO_LABEL[detail.priorita][0]}</span>}
                </DialogTitle>
                <DialogDescription className="text-base">
                  <b className="text-foreground">{detail.operatore_nome || "—"}</b> · {fmtDate(detail.data)} {detail.ora} · turno {bucketOf(detail.turno)}
                </DialogDescription>
              </DialogHeader>
              {(detail.ospiti_tag || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5">{detail.ospiti_tag.map((o) => <span key={o.id} className="text-sm font-semibold bg-blue-100 text-blue-700 rounded-full px-3 py-1">@{o.nome}</span>)}</div>
              )}
              {detail && <EventoTitolo c={detail} />}
              <p className={`text-base md:text-lg leading-relaxed whitespace-pre-wrap text-foreground py-2 ${detail?.evento_titolo ? "font-head font-bold" : ""}`} data-testid="consegna-detail-testo">{renderTesto(detail)}</p>
              <div className="pt-3 border-t text-sm text-muted-foreground">
                {(detail.letture || []).length === 0
                  ? <span className="inline-flex items-center gap-1"><Clock size={14} /> Non ancora letta</span>
                  : <span className="inline-flex items-center gap-1 text-green-700"><CheckCircle2 size={15} /> Letta da {(detail.letture || []).map((l) => `${l.user_name} (${fmtLet(l.created_at)})`).join(", ")}</span>}
              </div>
              <DialogFooter>
                {canArchivio && <Button variant="outline" onClick={() => setDelConsegna(detail)} data-testid="consegna-detail-del" className="text-rose-600 border-rose-200 hover:bg-rose-50"><Trash2 size={15} className="mr-1" /> Elimina</Button>}
                <Button variant="outline" onClick={() => printConsegna(detail)} data-testid="consegna-detail-print"><Printer size={15} className="mr-1" /> Stampa / Condividi</Button>
                <Button onClick={() => setDetail(null)} data-testid="consegna-detail-close">Chiudi</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nuova consegna</DialogTitle>
            <DialogDescription>Il nome dell'operatore e il testo sono obbligatori.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="text-xs font-semibold mb-1 block">Operatore che scrive *</Label>
              {signerName ? <OperatoreLocked name={signerName} testid="consegna-operatore" /> : (
              <Select value={form.operatore_nome || ""} onValueChange={(v) => setF("operatore_nome", v)}>
                <SelectTrigger data-testid="consegna-operatore"><SelectValue placeholder={operatori.length ? "Seleziona operatore" : "Seleziona prima la struttura"} /></SelectTrigger>
                <SelectContent>{operatori.map((o) => <SelectItem key={o.id} value={`${o.nome} ${o.cognome}`}>{o.nome} {o.cognome} ({o.qualifica})</SelectItem>)}</SelectContent>
              </Select>)}
            </div>
            <div><Label className="text-xs font-semibold mb-1 block">Data</Label><Input type="date" value={form.data || ""} onChange={(e) => setF("data", e.target.value)} /></div>
            <div><Label className="text-xs font-semibold mb-1 block">Ora</Label><Input type="time" value={form.ora || ""} onChange={(e) => setF("ora", e.target.value)} /></div>
            <div className="col-span-2">
              <Label className="text-xs font-semibold mb-1.5 block">Turno</Label>
              <div className="grid grid-cols-3 gap-2" data-testid="consegna-turno">
                {sezioni.map(([key, label, orario, bg, bar]) => {
                  const active = form.turno === key;
                  return (
                    <button key={key} type="button" onClick={() => { setF("turno", key); if (key === "notte") setNotteDlg({ data: form.data || todayIso }); }} data-testid={`consegna-turno-${key}`}
                      className={`rounded-xl border-2 px-3 py-3 text-center transition-all ${active ? "shadow-sm" : "hover:bg-secondary/60"}`}
                      style={active ? { borderColor: bar, background: bg } : { borderColor: "#e2e8f0" }}>
                      <span className="block text-sm font-bold" style={active ? { color: bar } : {}}>{label}</span>
                      <span className="block text-[11px] text-muted-foreground tabnum mt-0.5">{orario}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="col-span-2">
              <Label className="text-xs font-semibold mb-1.5 block">Priorità</Label>
              <div className="grid grid-cols-3 gap-2" data-testid="consegna-priorita">
                {[["normale", "Normale", "#64748b", "#f1f5f9"], ["importante", "Importante", "#d97706", "#fef3c7"], ["urgente", "Urgente", "#dc2626", "#fee2e2"]].map(([key, label, color, bg]) => {
                  const active = form.priorita === key;
                  return (
                    <button key={key} type="button" onClick={() => setF("priorita", key)} data-testid={`consegna-priorita-${key}`}
                      className={`rounded-xl border-2 px-3 py-3 text-center text-sm font-bold transition-all ${active ? "shadow-sm" : "hover:bg-secondary/60"}`}
                      style={active ? { borderColor: color, background: bg, color } : { borderColor: "#e2e8f0" }}>
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="col-span-2">
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs font-semibold block">Testo della consegna *</Label>
                <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1"><AtSign size={12} /> digita @ per taggare un ospite</span>
              </div>
              {tagged.filter((t) => (form.testo || "").includes(`@${t.nome}`)).length > 0 && (
                <div className="flex flex-wrap gap-1 mb-1.5">{tagged.filter((t) => (form.testo || "").includes(`@${t.nome}`)).map((t) => <span key={t.id} className="text-[11px] font-semibold bg-blue-100 text-blue-700 rounded-full px-2 py-0.5">@{t.nome}</span>)}</div>
              )}
              <div className="relative">
                <Textarea ref={taRef} value={form.testo || ""} onChange={onTestoChange} rows={16} className="min-h-[200px] sm:min-h-[340px] text-base leading-relaxed" placeholder="Scrivi qui la consegna in dettaglio...  (digita @ per taggare un ospite)" data-testid="consegna-testo" />
                {suggestions.length > 0 && (
                  <div className="absolute z-30 left-2 top-11 w-72 max-h-56 overflow-y-auto rounded-lg border bg-white shadow-lg" data-testid="consegna-mention-list">
                    {suggestions.map((o) => (
                      <button key={o.id} type="button" onClick={() => pickOspite(o)} data-testid={`consegna-mention-${o.id}`} className="w-full text-left px-3 py-2 text-sm hover:bg-secondary flex items-center gap-2">
                        <AtSign size={13} className="text-blue-600" /> {o.nome} {o.cognome}{o.camera ? <span className="text-xs text-muted-foreground ml-auto">Cam. {o.camera}</span> : null}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter className="sticky bottom-0 bg-white pt-3 -mx-6 px-6 pb-1 border-t sm:static sm:border-0 sm:bg-transparent sm:pt-0 sm:mx-0 sm:px-0">
            <Button variant="outline" onClick={() => setOpen(false)} className="gr-mobile-tap">Annulla</Button>
            <Button onClick={save} data-testid="save-consegna" className="font-semibold gr-mobile-tap">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!futureWarn} onOpenChange={(o) => !o && setFutureWarn(null)}>
        <DialogContent className="max-w-md border-4 border-amber-400" data-testid="consegna-future-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700"><AlertTriangle size={22} /> Non si possono fare consegne nel futuro</DialogTitle>
            <DialogDescription className="text-base pt-1">
              {futureWarn?.reason === "giorno"
                ? "Hai scelto un giorno futuro. Le consegne si registrano solo per eventi già avvenuti: correggi la data a oggi o a un giorno passato."
                : <>Il turno <b>{SEZIONI.find(([k]) => k === futureWarn?.turno)?.[1] || futureWarn?.turno}</b> di oggi non è ancora iniziato, quindi non è ancora avvenuto. Potrai scrivere questa consegna solo quando il turno sarà cominciato (oppure scegli un turno già in corso o passato).</>}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setFutureWarn(null)} data-testid="consegna-future-ok" className="w-full bg-amber-500 hover:bg-amber-600 font-semibold">Ho capito</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!notteDlg} onOpenChange={(o) => !o && setNotteDlg(null)}>
        <DialogContent className="max-w-md" data-testid="consegna-notte-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Moon size={18} className="text-violet-600" /> Data della consegna notturna</DialogTitle>
            <DialogDescription>
              Il turno di notte è a cavallo di due giorni. Controlla che la data sia corretta: spesso una consegna della notte appena trascorsa va segnata con la data di <b>ieri</b>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg border bg-secondary/30 p-3 text-center">
              <div className="text-xs text-muted-foreground">Data selezionata</div>
              <div className="text-lg font-head font-bold capitalize" data-testid="consegna-notte-data-label">{fmtGiornoLungo(notteDlg?.data)}</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setNotteDlg((d) => ({ ...d, data: ieri }))} data-testid="consegna-notte-ieri"
                className={`rounded-xl border-2 px-3 py-3 text-center transition-all ${notteDlg?.data === ieri ? "border-amber-500 bg-amber-50 shadow-sm" : "border-slate-200 hover:bg-secondary/60"}`}>
                <span className="block text-xs font-bold uppercase tracking-wide text-amber-700">Notte di ieri</span>
                <span className="block text-sm font-semibold capitalize mt-0.5">{fmtGiornoLungo(ieri)}</span>
              </button>
              <button type="button" onClick={() => setNotteDlg((d) => ({ ...d, data: todayIso }))} data-testid="consegna-notte-oggi"
                className={`rounded-xl border-2 px-3 py-3 text-center transition-all ${notteDlg?.data === todayIso ? "border-emerald-500 bg-emerald-50 shadow-sm" : "border-slate-200 hover:bg-secondary/60"}`}>
                <span className="block text-xs font-bold uppercase tracking-wide text-emerald-700">Notte di oggi</span>
                <span className="block text-sm font-semibold capitalize mt-0.5">{fmtGiornoLungo(todayIso)}</span>
              </button>
            </div>
            <div>
              <Label className="text-xs font-semibold mb-1 block">Oppure scegli un'altra data</Label>
              <Input type="date" value={notteDlg?.data || ""} onChange={(e) => setNotteDlg((d) => ({ ...d, data: e.target.value }))} data-testid="consegna-notte-date-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNotteDlg(null)} data-testid="consegna-notte-cancel">Annulla</Button>
            <Button onClick={() => { setF("data", notteDlg.data); setNotteDlg(null); }} data-testid="consegna-notte-confirm" className="font-semibold">Conferma data</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delConsegna} onOpenChange={(o) => !o && setDelConsegna(null)}>
        <DialogContent className="max-w-md" data-testid="consegna-del-dialog">
          <DialogHeader>
            <DialogTitle>Eliminare questa consegna?</DialogTitle>
            <DialogDescription>
              La consegna verrà spostata nell'<b>Archivio</b> e non sarà più visibile nell'elenco. Potrai ripristinarla in qualsiasi momento dall'Archivio.
            </DialogDescription>
          </DialogHeader>
          {delConsegna && (
            <div className="rounded-lg border bg-secondary/30 p-3 text-sm">
              <div className="text-[11px] text-muted-foreground mb-1">{delConsegna.operatore_nome || "—"} · {fmtDate(delConsegna.data)} {delConsegna.ora}</div>
              <div className="line-clamp-3 whitespace-pre-wrap">{delConsegna.testo}</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelConsegna(null)} data-testid="consegna-del-cancel">Annulla</Button>
            <Button onClick={() => eliminaConsegna(delConsegna)} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="consegna-del-confirm"><Trash2 size={15} className="mr-1" /> Elimina</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={archivioOpen} onOpenChange={setArchivioOpen}>
        <DialogContent className="w-[95vw] max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="consegne-archivio-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Archive size={20} className="text-primary" /> Archivio consegne</DialogTitle>
            <DialogDescription>Consegne eliminate. Puoi ripristinarle per riportarle nell'elenco.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col sm:flex-row gap-2 mb-1">
            <Input value={archQ} onChange={(e) => setArchQ(e.target.value)} placeholder="Cerca testo o operatore…" className="flex-1" data-testid="archivio-search" />
            <Input type="date" value={archData} onChange={(e) => setArchData(e.target.value)} className="sm:w-44" data-testid="archivio-date" />
            <Select value={archTurno} onValueChange={setArchTurno}>
              <SelectTrigger className="sm:w-40" data-testid="archivio-turno"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tutti">Tutti i turni</SelectItem>
                <SelectItem value="mattina">Mattina</SelectItem>
                <SelectItem value="pomeriggio">Pomeriggio</SelectItem>
                <SelectItem value="notte">Notte</SelectItem>
              </SelectContent>
            </Select>
            {(archQ || archData || archTurno !== "tutti") && <Button variant="ghost" onClick={() => { setArchQ(""); setArchData(""); setArchTurno("tutti"); }} data-testid="archivio-clear">Azzera</Button>}
          </div>
          <div className="space-y-2.5">
            {archivioFiltered.length === 0 && <div className="text-sm text-muted-foreground text-center py-8">Nessuna consegna in archivio.</div>}
            {archivioFiltered.map((c) => (
              <div key={c.id} className="rounded-lg border bg-white p-3 flex items-start gap-3" data-testid={`archivio-consegna-${c.id}`} style={{ borderLeft: `3px solid ${ACCENT[c.priorita] || ACCENT.normale}` }}>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] text-muted-foreground mb-1">{c.operatore_nome || "—"} · {fmtDate(c.data)} {c.ora} · turno {bucketOf(c.turno)}{c.deleted_by ? ` · eliminata da ${c.deleted_by}` : ""}</div>
                  <ConsegnaTesto c={c} />
                </div>
                <Button size="sm" variant="outline" onClick={() => ripristinaConsegna(c)} data-testid={`archivio-restore-${c.id}`} className="shrink-0"><RotateCcw size={14} className="mr-1" /> Ripristina</Button>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={() => setArchivioOpen(false)} data-testid="consegne-archivio-close">Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
